<template>
    <div>
        <datatable
            :url="liabilitiesUrl"
            :reportsUrl="liabilitiesReportsUrl"
            :columns="tColumns"
            :options="tOptions"
            @checked="onChecked">

          <template slot="amount" slot-scope="props">
            <!-- Here we call a method on click and we pass a row data from the slot's properties -->
            <span>{{formatAmount(props.row.amount)}}</span>
          </template>

          <template slot="creationDate" slot-scope="props">
            <!-- Here we call a method on click and we pass a row data from the slot's properties -->
            <span>{{formatDate(props.row.creationDate)}}</span>
          </template>

          <template slot="dueDate" slot-scope="props">
            <!-- Here we call a method on click and we pass a row data from the slot's properties -->
            <span v-if="props.row.dueDate">{{formatDate(props.row.dueDate)}}</span>
            <span v-else>-</span>
          </template>

          <template slot="settledAmount" slot-scope="props">
            <span>{{formatAmount(props.row.settledAmount)}}</span>
          </template>

          <template slot="status" slot-scope="props">
            <!-- Here we call a method on click and we pass a row data from the slot's properties -->
            <button v-if="props.row.settled && props.row.amount > 0" class="btn btn-sm btn-success">{{$t('invoices.invoiceSettled')}}</button>
            <button v-else-if="props.row.status === InvoiceStatus.SETTLED" class="btn btn-sm btn-success">{{$t('invoices.invoiceSettled')}}</button>
            <button v-else-if="props.row.status === InvoiceStatus.ACTIVE || props.row.status === InvoiceStatus.PROINVOICE" class="btn btn-sm btn-info">{{$t('invoices.invoiceStatus.' + props.row.status)}}</button>
            <button v-else-if="props.row.status === InvoiceStatus.PARTIALLY_SETTLED" class="btn btn-sm btn-info">{{$t('invoices.invoiceStatus.' + props.row.status)}}</button>
            <button v-else-if="props.row.status === InvoiceStatus.ASSIGNED" class="btn btn-sm btn-warning">{{$t('invoices.invoiceStatus.' + props.row.status)}}</button>
            <button v-else class="btn btn-sm" v-bind:class="{ 'btn-danger': props.row.status === InvoiceStatus.CANCELLED }">{{$t('invoices.invoiceStatus.' + props.row.status)}}</button>
          </template>

          <template slot="actions" slot-scope="props">
            <!-- Here we call a method on click and we pass a row data from the slot's properties -->
            <b-button variant="outline-info" @click="invoiceClicked(props.row)">{{$t('invoices.review')}}</b-button>
          </template>
        </datatable>

        <div class="text-right" v-if="optionsVisible">
          <hr>
          <b-dropdown :text="$t('invoices.invoiceExport')" variant="success" class="m-md-2" split :disabled="!hasInvoiceExportClaim || load" v-on:click="exportToJson()">
            <b-dropdown-item-button v-on:click="exportToCsv()">{{$t('invoices.invoiceExportCsv')}}</b-dropdown-item-button>
            <b-dropdown-item-button v-on:click="exportToJson()">{{$t('invoices.invoiceExportJson')}}</b-dropdown-item-button>
          </b-dropdown>

          <b-btn variant="success" v-b-modal.cancelInvoicesModal :disabled="!hasInvoiceCancelClaim || load">
            {{ $t('invoices.cancelInvoices') }}
            <loader :show="load" type="button"></loader>
          </b-btn>
        </div>

      <!-- Modal Component -->
      <b-modal id="cancelInvoicesModal" :title="$t('invoices.cancelInvoice')" @ok="cancelInvoices" :ok-title="$t('forms.apply')" :cancel-title="$t('forms.cancel')">

        <form @submit.stop.prevent="cancelInvoice">
          <b-form-textarea type="text" :rows=5 v-model="cancelComments" :placeholder="$t('invoices.cancellationComment')"/>
        </form>

      </b-modal>

    </div>

</template>

<script>
  import Vue from 'vue'
  import Datatable from '@/components/misc/datatable/Datatable'
  import ContextHeader from '@/components/misc/context-header/ContextHeader'
  import { EventBus } from '@/router/bus'
  import i18n from '@/i18n'
  import { hasInvoiceCancelClaim, hasInvoiceExportClaim } from '@/router/auth'
  import Loader from '@/components/misc/Loader'

  export default {
    name: 'admin-invoices',
    components: {
      Datatable,
      ContextHeader,
      Loader
    },
    data () {
      return {
        load: false,
        InvoiceStatus: {
          ACTIVE: 1,
          INVALID: 2,
          CANCELLED: 3,
          PARTIALLY_SETTLED: 4,
          SETTLED: 5,
          ASSIGNED: 6,
          PROINVOICE: 7,
          OPEN: 101
        },
        tOptions: {
          sortable: true, // Show sortable arrows
          filter: true, // Show filter fields
          checkboxes: true, // Show checkboxes
          checkboxValue: 'invoiceId', // Which value should checkboxes use
          checkboxesPersist: false, // Should checked values persists after new request
          per_page_options: [5, 10, 15, 25, 50], // Values that will be shown i the select list
          per_page_default: 5,
          payloadsTotalPropertyName: 'additionalInformation', // Name of the the payload property that returns total num of rows (total, count,..)
          additionalQueryParameter: 'side=creditor',
          statuses: this.InvoiceStatus,
          partialCount: true,
          invalidRowMarker: {
            criteria: function (row, statuses) {
              return row['status'] === statuses.CANCELLED || row['status'] === statuses.INVALID
            }
          },
          highlightedRowMarker: {
            criteria: function (row, statuses) {
              return row['status'] === statuses.ASSIGNED
            }
          },
          detailsLink: {
            createLink: function (row) {
              return 'invoices/' + row['invoiceId']
            }
          }
        },
        tColumns: [
          {
            columnName: 'formattedInvoiceNumber',
            title: i18n.t('invoices.columnInvoiceNumber'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.invoiceNumber
            }
          },
          {
            columnName: 'creditorName',
            title: i18n.t('invoices.columnCreditor'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.creditorName
            }
          },
          {
            columnName: 'debtorCompanyNumber',
            title: i18n.t('invoices.columnBudgetIdNumber'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.debtorCompanyNumber
            }
          },
          {
            columnName: 'debtorName',
            title: i18n.t('invoices.columnDebtor'),
            filterType: 'input',
            align: 'center',
            cell (row) {
              return row.debtorName
            }
          },
          {
            columnName: 'amount',
            columnRange: [
              {
                text: i18n.t('invoices.columnInvoiceAmountFrom'),
                value: 'amount-from'
              },
              {
                text: i18n.t('invoices.columnInvoiceAmountTo'),
                value: 'amount-to'
              }
            ],
            title: i18n.t('invoices.columnInvoiceAmount'),
            filterType: 'rangeMoney',
            hideFilter: true,
            showSummary: true,
            align: 'left',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'settledAmount',
            columnRange: [
              {
                text: i18n.t('invoices.columnInvoiceAmountFrom'),
                value: 'settledAmount-from'
              },
              {
                text: i18n.t('invoices.columnInvoiceAmountTo'),
                value: 'settledAmount-to'
              }
            ],
            title: i18n.t('invoices.columnSettledAmount'),
            filterType: 'rangeMoney',
            hideFilter: true,
            showSummary: true,
            align: 'left',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'creationDate',
            columnRange: [
              {
                text: i18n.t('invoices.columnCreationDateRangeFrom'),
                value: 'creationDate-from'
              },
              {
                text: i18n.t('invoices.columnCreationDateRangeTo'),
                value: 'creationDate-to'
              }
            ],
            title: i18n.t('invoices.columnCreationDate'),
            filterType: 'date-range',
            hideFilter: true,
            align: 'center',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'dueDate',
            columnRange: [
              {
                text: i18n.t('invoices.columnDueDateRangeFrom'),
                value: 'dueDate-from'
              },
              {
                text: i18n.t('invoices.columnDueDateRangeTo'),
                value: 'dueDate-to'
              }
            ],
            title: i18n.t('invoices.columnDueDate'),
            filterType: 'date-range',
            hideFilter: true,
            align: 'center',
            cell (row) {
              return null
            }
          },
          {
            columnName: 'status',
            title: i18n.t('invoices.columnInvoiceStatus'),
            filterType: 'select',
            filterSelectOptions: [
              { key: i18n.t('invoices.invoiceStatus.0'), value: '' },
              { key: i18n.t('invoices.invoiceStatus.1'), value: 1 },
              { key: i18n.t('invoices.invoiceStatus.2'), value: 2 },
              { key: i18n.t('invoices.invoiceStatus.3'), value: 3 },
              { key: i18n.t('invoices.invoiceStatus.4'), value: 4 },
              { key: i18n.t('invoices.invoiceStatus.5'), value: 5 },
              { key: i18n.t('invoices.invoiceStatus.6'), value: 6 },
              { key: i18n.t('invoices.invoiceStatus.7'), value: 7 },
              { key: i18n.t('invoices.invoiceStatus.101'), value: 101 }
            ],
            cell (row) {
              return null
            }
          }
        ],
        optionsVisible: false,
        cancelComments: '',
        selectedInvoices: [],
        liabilitiesUrl: '',
        liabilitiesReportsUrl: '',
        hasInvoiceCancelClaim: false,
        hasInvoiceExportClaim: false
      }
    },
    created: function () {
      this.liabilitiesUrl = Vue.$apiConfig.pagedInvoiceLiabilitiesGet
      this.liabilitiesReportsUrl = Vue.$config.reporting_api_root + '/' + Vue.$apiConfig.generateInvoiceLiabilitiesReport

      this.hasInvoiceCancelClaim = hasInvoiceCancelClaim()
      this.hasInvoiceExportClaim = hasInvoiceExportClaim()
      this.tOptions.statuses = this.InvoiceStatus
    },
    methods: {
      onChecked (checkedValues) {
        this.selectedInvoices = checkedValues
        this.optionsVisible = checkedValues.length > 0
      },
      cancelInvoices: function (e) {
        this.load = true
        let request = []

        for (var i = 0; i < this.selectedInvoices.length; i++) {
          request.push({ InvoiceId: this.selectedInvoices[i], CancelComments: this.cancelComments })
        }

        this.$http.post(Vue.$apiConfig.cancelInvoicesPost, request)
          .then(response => {
            this.logConsole(response)
            this.load = false
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('invoices.cancellationSuccess'),
              errors: []
            })
            EventBus.$emit('refresh-table')
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      exportToCsv: function () {
        this.logConsole('Exporting invoices' + this.selectedInvoices)
        this.load = true
        this.saveToFile(Vue.$config.reporting_api_root + '/' + Vue.$apiConfig.exportInvoiceCsv, this.selectedInvoices, 'export.csv', this.stopLoader, this.convertCSVseparator)
      },
      exportToJson: function () {
        this.logConsole('Exporting invoices' + this.selectedInvoices)
        this.load = true
        this.saveToFile(Vue.$config.reporting_api_root + '/' + Vue.$apiConfig.exportInvoiceJson, this.selectedInvoices, 'export.json', this.stopLoader)
      },
      stopLoader () {
        this.load = false
      }
    }
  }

</script>

<style lang="scss" type="text/scss" scoped>

  @import '~@/assets/scss/variables.scss';

  .bg-primary {
    background-color: $primary-color-accent !important;
  }
  
  .text-dark {
    font-weight: 600;
    font-size: 12pt;
    color: $font-color-dark !important;
  }

  #cancelInvoicesModal {
    margin-top: 85px !important;
  }

  @media (min-width: 1400px) {
      .container {
          width: 1360px;
          max-width: 100%;
      }
  }

  @media (min-width: 1680px) {
      .container {
          width: 1640px;
          max-width: 100%;
      }
  }

  @media (min-width: 1920px) {
      .container {
          width: 1860px;
          max-width: 100%;
      }
  }

  @media (min-width: 2560px) {
      .container {
          width: 2460px;
          max-width: 100%;
      }
  }

  @media (min-width: 2880px) {
      .container {
          width: 2620px;
          max-width: 100%;
      }
  }

  @media (min-width: 3840px) {
      .container {
          width: 3550px;
          max-width: 100%;
      }
  }    
  
</style>



// WEBPACK FOOTER //
// AdminInvoices.vue?33458056